/**
 * Typen des Leitfadens der Planungsphase (docs/planungsphase-leitfaden-plan.md).
 *
 * Der Leitfaden ist eine ANSICHT ueber denselben Daten wie die Reiter, kein
 * gespeicherter Zustand: jede Station rechnet ihren Stand aus Phase, Fragen,
 * Hinweisen und Unterlagen (`leitfadenStand` in leitfaden.ts). Deshalb kann
 * man jederzeit hinaus, und beim naechsten Oeffnen — auf jedem Geraet, fuer
 * jeden Kollegen — steht die Station von selbst wieder da, an der etwas zu
 * tun ist. Im Browser liegt nur Bequemlichkeit (leitfadenSpeicher.ts). Der
 * einzige Vermerk des Leitfadens selbst — „Rest bleibt offen" (Station 3) —
 * steht am Kopfsatz (`ProjektMerkmale.leitfadenRestOffen`), nicht im Browser.
 */

import type {
    PlanungUnterlage,
    Planungsfrage,
    Planungshinweis,
    Planungsphase,
    SigeSchrittDokumente,
} from './types';

/** Die neun Stationen in Ablaufreihenfolge. Die Schluessel stehen im Browser-
 *  Merker — nie umbenennen, nur ergaenzen. */
export type StationSchluessel =
    | 'beschreiben'   // 1 Vorhaben beschreiben
    | 'umfang'        // 2 Umfang und Pflichten
    | 'merkmale'      // 3 Merkmale
    | 'katalog'       // 4 Fragenkatalog erzeugen
    | 'versenden'     // 5 Klaerungslisten versenden
    | 'antworten'     // 6 Antworten einarbeiten
    | 'unterlagen'    // 7 Unterlagen
    | 'bedenken'      // 8 Bedenken
    | 'abschluss';    // 9 Uebergabe und Abschluss

export const STATION_REIHENFOLGE: ReadonlyArray<StationSchluessel> = [
    'beschreiben', 'umfang', 'merkmale', 'katalog', 'versenden', 'antworten', 'unterlagen', 'bedenken', 'abschluss',
];

/** A Vorhaben · B Fragen · C Nachweise · D Abschluss. */
export type LeitfadenAbschnitt = 'A' | 'B' | 'C' | 'D';

/**
 *   erledigt           — Kriterium erfuellt (ggf. mit `hinweis`)
 *   aktuell            — hier ist die naechste Handlung (genau eine Station,
 *                        und nur, wenn sie sonst `offen` waere)
 *   offen              — noch zu tun, aber nicht als naechstes
 *   wartet             — nichts zu tun, Fristen laufen (`naechsteFrist`)
 *   handlungsbedarf    — dieselben harten Gruende wie die rote Ampel
 *   nicht_erforderlich — derzeit nichts zu tun und nichts zu erwarten
 *   gesperrt           — Voraussetzung fehlt (`sperrgrund`)
 */
export type StationZustand =
    | 'erledigt'
    | 'aktuell'
    | 'offen'
    | 'wartet'
    | 'handlungsbedarf'
    | 'nicht_erforderlich'
    | 'gesperrt';

export interface StationStand {
    schluessel: StationSchluessel;
    /** 1–9. */
    nummer: number;
    abschnitt: LeitfadenAbschnitt;
    zustand: StationZustand;
    /** Eine Zeile fuer Leiste, Kachel und Aufgabenliste, z. B.
     *  „2 Fristen abgelaufen — feststellen". Leer, wenn nichts zu sagen ist. */
    kurz: string;
    /** Gelber Vermerk trotz `erledigt` (offene Merkmale, nachziehbare Fragen,
     *  ungeklaerte Pflicht ausserhalb des Rot-Fensters). */
    hinweis: boolean;
    /** Nur bei `gesperrt`: warum, als ganzer Satz. Sonst `null`. */
    sperrgrund: string | null;
    /** Nur bei `wartet`: ISO-Tag (YYYY-MM-DD) der naechsten Frist, sonst `null`. */
    naechsteFrist: string | null;
}

export interface LeitfadenEingang {
    /** Der GESPEICHERTE Kopfsatz — nie der Entwurf der Maske. */
    phase: Planungsphase | null;
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
    /** Heutiger Tag in Ortszeit (`heuteLokalIso`), YYYY-MM-DD. */
    heuteIso: string;
    /** Ausfuehrungsbeginn, Rueckfall Projektstart (`baustartAusGlobalData`). */
    baustartIso: string | null;
    /** Die Checkliste, wie sie gerade gilt (`useUnterlagenStand().liste`);
     *  fehlt sie, gilt `phase.unterlagen`. */
    unterlagen?: PlanungUnterlage[];
    /** Mitgeltende Dokumente des SiGe-Plans, sofern geladen — sonst zaehlen nur
     *  die aus den Merkmalen erforderlichen Unterlagen (wie die Sammelampel). */
    sigeSchritte?: readonly SigeSchrittDokumente[];
    /** Offene Fassungsfragen des Aenderungsdienstes; nur Vermerk an Station 9. */
    offeneFassungen?: number;
    // „Rest bleibt offen" (Station 3) ist kein Eingang: der Vermerk steht am
    // Kopfsatz (`phase.merkmale.leitfadenRestOffen`, `restOffenVermerkt`).
}

/** Der Browser-Merker je Projekt: zuletzt besuchte Station. */
export interface LeitfadenMerker {
    station: StationSchluessel;
    /** ISO-Zeitpunkt des letzten Besuchs. */
    am: string;
}

/** Die beiden Ansichten des Moduls. */
export type PlanungAnsichtArt = 'leitfaden' | 'reiter';
